import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { IdentidadePortalService } from 'app/features/portal-carta-servico/services/identidade-portal.service';
import { RedesSociaisPublicasService } from 'app/features/portal-carta-servico/services/redes-sociais-publicas.service';
import { RodapePublicoService } from 'app/features/portal-carta-servico/services/rodape-publico.service';

/** Um link do acesso rápido: interno vira `UrlTree`, para `?` e `#` funcionarem no `routerLink`. */
interface LinkRodapeTela {
  rotulo: string;
  destinoInterno: UrlTree | null;
  href: string | null;
  novaAba: boolean;
}

/**
 * O rodapé de todas as páginas, com o que o órgão cadastrou na tela Rodapé do adm e o brasão
 * e o nome publicados. Cada coluna só aparece com conteúdo; sem nenhuma, fica o copyright.
 */
@Component({
  selector: 'app-main-footer',
  templateUrl: './main-footer.component.html',
  styleUrls: ['./main-footer.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MainFooterComponent {
  private readonly router = inject(Router);
  private readonly identidade = inject(IdentidadePortalService);
  private readonly rodapePublico = inject(RodapePublicoService);
  private readonly redesSociais = inject(RedesSociaisPublicasService);

  readonly brasaoIndisponivel = signal(false);

  readonly anoAtual = new Date().getFullYear();

  private readonly rodape = this.rodapePublico.rodape;

  readonly nome = computed(() => (this.rodape().exibeNome ? this.identidade.nome() : null));

  readonly brasaoUrl = computed(() =>
    this.rodape().exibeBrasao && !this.brasaoIndisponivel() ? this.identidade.brasaoUrl() : null,
  );

  /** Vazio quando o nome aparece ao lado: o leitor de tela já lê o nome. */
  readonly altBrasao = computed(() => {
    if (this.nome()) return '';

    const nome = this.identidade.nome();
    return nome ? `Brasão: ${nome}` : 'Brasão';
  });

  readonly mostraMarca = computed(() => !!this.brasaoUrl() || !!this.nome());

  readonly links = computed<readonly LinkRodapeTela[]>(() =>
    this.rodape().links.map((link) => ({
      rotulo: link.rotulo,
      destinoInterno: link.interno ? this.router.parseUrl(link.link) : null,
      href: link.interno ? null : link.link,
      novaAba: link.novaAba,
    })),
  );

  readonly endereco = computed(() => this.rodape().endereco);

  readonly horario = computed(() => this.rodape().horario);

  readonly mostraEndereco = computed(() => this.endereco().length > 0 || !!this.horario());

  readonly contatos = computed(() => this.rodape().contatos);

  readonly mostraRedes = computed(() => this.rodape().exibeRedes && this.redesSociais.redes().length > 0);

  readonly mostraContato = computed(() => this.contatos().length > 0 || this.mostraRedes());

  readonly temColunas = computed(
    () => this.mostraMarca() || this.links().length > 0 || this.mostraEndereco() || this.mostraContato(),
  );
}
